<script setup>
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { RouterView, useRoute, useRouter } from 'vue-router';

import { fetchCsrfBootstrap, logout } from '@/api/auth.js';
import AppShell from '@/components/AppShell.vue';
import AriaAnnouncer from '@/components/ds/AriaAnnouncer.vue';
import SkipToContent from '@/components/ds/SkipToContent.vue';
import { useViewport } from '@/composables/useViewport.js';
import { useAuthStore } from '@/stores/auth.js';
import { useTenantStore } from '@/stores/tenant.js';
import MobileFallbackView from '@/views/MobileFallbackView.vue';

const auth = useAuthStore();
const tenant = useTenantStore();
const { isAuthenticated } = storeToRefs(auth);
const route = useRoute();
const router = useRouter();
const { isMobile } = useViewport();

const showShell = computed(() => {
  if (!isAuthenticated.value) return false;
  return route.meta?.shell !== false;
});

// Mobile intercept (UX-DR26 / FR47): authenticated routes below 768 px land
// on the best-viewed-on-desktop view. Login is the only fully responsive
// surface; public routes (login + browser-unsupported) bypass the intercept.
const showMobileIntercept = computed(() => (
  isAuthenticated.value
  && isMobile.value
  && route.meta?.shell !== false
));

async function onLogout() {
  // 1. Destruir la sesión en el servidor — sin esto el cookie HttpOnly
  //    `session_id` sobrevive y un refresh re-autentica vía /auth/session.
  //    /auth/logout es mutating → requiere CSRF.
  try {
    const { csrfToken } = await fetchCsrfBootstrap();
    await logout(csrfToken);
  } catch {
    // Best-effort: si el backend no responde igual limpiamos el
    // estado local y mandamos al login — no dejamos al usuario atrapado.
  }
  // 2. Limpiar estado local y abortar fetches de tenant en vuelo.
  auth.clear();
  tenant.abortAll('logout');
  // 3. Redirigir explícitamente — al perder el shell la ruta actual
  //    seguiría montada en <main> sin esta navegación.
  router.push({ path: '/login' });
}
</script>

<template>
  <MobileFallbackView
    v-if="showMobileIntercept"
    @logout="onLogout"
  />

  <AppShell
    v-else-if="showShell"
    @logout="onLogout"
  >
    <RouterView />
  </AppShell>

  <template v-else>
    <SkipToContent target="main" />
    <main id="main">
      <RouterView />
    </main>
  </template>

  <AriaAnnouncer />
</template>
